import React from "react";
import { Route, Routes, Navigate } from "react-router-dom";

import RegistrationCard from "pages/registration/Registration";
import LoginCard from "pages/login/Login";
import ResetPassword from "pages/reset_password/ResetPassword";
import ResetPasswordConfirm from "pages/reset_password/ResetPasswordConfirm";
import ResendConfirmation from "pages/resend_confirmation/ResendConfirmation";
import MyClasses from "pages/my_classes/MyClasses";
import ClassDetails from "pages/class_details/ClassDetails";
import TopicDetailsPage from "pages/topic_details/TopicDetailsPage";
import MyLevel from "pages/my_level/MyLevel";
import Support from "pages/support/Support";
import BookAClass from "pages/book_a_class/BookAClass";
import BookACourse from "pages/book_a_course/BookACourse";
import NewBookACourse from "pages/new-book-a-course/BookACourse";
import TeacherProfile from "pages/teacher_profile";
import MyCredits from "pages/my_credits";
import Referral from "pages/referral/Referral";
import ChapterDetailsPage from "pages/chapter-details/ChapterDetailsPage";
import ConfirmRegistration from "pages/confirm_registration/ConfirmRegistration";
import VnpayPaymentReturnUrl from "pages/vnpay/VnpayPaymentReturnUrl";

import NewSignUp from "pages/new_sign_up/NewSignUp";
import AfterRegistration from "pages/new_sign_up/after_sign_up/AfterRegistration";
import WordpressFragment from "pages/wordpress_fragment/WordpressFragment";
// Account pages
import MyProfile from "pages/account/my_profile";
import MySettings from "pages/account/my_settings";
import MyBillings from "pages/account/my_billings";
import MyCertificates from "pages/account/my_certificates";
import MyMailbox from "pages/account/my_mailbox";
import AccountLayout from "pages/account";

// View all
import ViewAll from "pages/view_all/ViewAll";
import PastClasses from "pages/view_all/PastClasses";
import FutureClasses from "pages/view_all/FutureClasses";

// Admin login
import ByPass from "ByPass";

import ProtectedRoute from "hoc/ProtectedRoute";
import UnprotectedRoute from "hoc/UnprotectedRoute";
import usePageView from "hooks/usePageView";
import Referer from "pages/account/refer_a_friend/Referer";
import JaaSMeeting from "pages/jaas_meeting/JaaSMeeting";
import RedirectHandling from "./RedirectHandling";
import Page404 from "./pages/404";
import Page500 from "./pages/500";

type TProps = {
  setLoggedIn: (value: boolean) => void;
};

const AppRouter = ({ setLoggedIn }: TProps) => {
  usePageView();

  const routes = [
    "/flexi-registration/:courseDetails",
    "/vi/flexi-registration/:courseDetails",
  ];

  return (
    <Routes>
      <Route path="/" element={<Navigate to="/my-classes" />} />
      <Route
        path="/register"
        element={
          <UnprotectedRoute>
            <RegistrationCard setLoggedIn={setLoggedIn} />
          </UnprotectedRoute>
        }
      />
      <Route
        path="/after-register"
        element={
          <ProtectedRoute>
            <AfterRegistration setLoggedIn={setLoggedIn} />
          </ProtectedRoute>
        }
      />
      <Route
        path="/wordpress-fragment"
        element={
          <ProtectedRoute>
            <WordpressFragment />
          </ProtectedRoute>
        }
      />
      {routes.map((path) => (
        <Route
          key={path}
          path={path}
          element={
            <UnprotectedRoute>
              <NewSignUp setLoggedIn={setLoggedIn} />
            </UnprotectedRoute>
          }
        />
      ))}
      <Route
        path="/new-sign-up"
        element={
          <UnprotectedRoute>
            <NewSignUp setLoggedIn={setLoggedIn} />
          </UnprotectedRoute>
        }
      />
      <Route
        path="/return_url"
        element={<VnpayPaymentReturnUrl setLoggedIn={setLoggedIn} />}
      />
      <Route
        path="/login"
        element={
          <UnprotectedRoute>
            <LoginCard setLoggedIn={setLoggedIn} />
          </UnprotectedRoute>
        }
      />
      <Route path="/meeting" element={<JaaSMeeting />} />
      <Route path="/confirm-registration" element={<ConfirmRegistration />} />
      <Route
        path="/reset-password"
        element={
          <UnprotectedRoute>
            <ResetPassword />
          </UnprotectedRoute>
        }
      />
      <Route
        path="/reset-password-confirm"
        element={<ResetPasswordConfirm />}
      />
      <Route path="/resend-confirmation" element={<ResendConfirmation />} />
      <Route
        path="/my-classes"
        element={
          <ProtectedRoute>
            <MyClasses />
          </ProtectedRoute>
        }
      />
      <Route
        path="/class-details/:classid"
        element={
          <ProtectedRoute>
            <ClassDetails />
          </ProtectedRoute>
        }
      />
      <Route
        path="/topic-details/:topicId"
        element={
          <ProtectedRoute>
            <TopicDetailsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/chapter-details/:chapterId"
        element={
          <ProtectedRoute>
            <ChapterDetailsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/referral/:referralCode"
        element={
          <UnprotectedRoute>
            <Referral />
          </UnprotectedRoute>
        }
      />
      <Route
        path="view-all"
        element={
          <ProtectedRoute>
            <ViewAll />
          </ProtectedRoute>
        }
      >
        <Route path="my-future-classes" element={<FutureClasses />} />
        <Route path="my-past-classes" element={<PastClasses />} />
      </Route>
      <Route path="my-level">
        <Route
          path=":levelId"
          element={
            <ProtectedRoute>
              <MyLevel />
            </ProtectedRoute>
          }
        />
        <Route
          path=""
          element={
            <ProtectedRoute>
              <MyLevel />
            </ProtectedRoute>
          }
        />
      </Route>
      <Route
        path="/support"
        element={
          <ProtectedRoute>
            <Support />
          </ProtectedRoute>
        }
      />
      <Route
        path="/book-class"
        element={
          <ProtectedRoute>
            <BookAClass />
          </ProtectedRoute>
        }
      />
      <Route
        path="/book-course"
        element={
          <ProtectedRoute>
            <BookACourse />
          </ProtectedRoute>
        }
      />
      {/* New testing route. Note: the route will be moved to /book-course after development and testing */}
      <Route
        path="/new-book-course"
        element={
          <ProtectedRoute>
            <NewBookACourse />
          </ProtectedRoute>
        }
      />
      <Route
        path="/teacher/:teacherId"
        element={
          <ProtectedRoute>
            <TeacherProfile />
          </ProtectedRoute>
        }
      />
      <Route
        path="/my-credits"
        element={
          <ProtectedRoute>
            <MyCredits />
          </ProtectedRoute>
        }
      />
      <Route
        path="account"
        element={
          <ProtectedRoute>
            <AccountLayout />
          </ProtectedRoute>
        }
      >
        <Route path="my-profile" element={<MyProfile />} />
        <Route path="my-settings" element={<MySettings />} />
        <Route path="my-billings" element={<MyBillings />} />
        <Route path="my-certificates" element={<MyCertificates />} />
        <Route path="my-mailbox" element={<MyMailbox />} />
        <Route path="refer-a-friend" element={<Referer />} />
      </Route>
      <Route path="/by-pass" element={<ByPass />} />
      <Route path="/admin" element={<RedirectHandling />} />
      <Route path="/teaching" element={<RedirectHandling />} />
      <Route path="/error" element={<Page500 />} />
      <Route element={<Page404 />} />
    </Routes>
  );
};

export default AppRouter;
